import React, { Fragment, ReactElement } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components
import Form from '@lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators and Selectors
import { changeData } from '@actions/ssap/ssapActionCreators';
import { getIsState, getServerDate } from '@reducers/appReducer';

// Utils
import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';
import { questionsVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';

// Constants
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { DATE_FORMAT_DASHED, TIME_INTERVALS } from '@utils/dates/dateMethods';

// Types
import {
    ApplicationStore,
    HouseholdMember,
    IsState,
    SsapPage,
    SsapReduxActions
} from '@components/ssap/SsapTypes';

import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

type IFieldChangeArgs = [
    newData: any,
    indexInArray: number | undefined,
    fields: string[] | undefined,
    fieldInfo: [name: string, value: any, disabled?: boolean, text?: string]
];
type EligibleMemberEntry = { hhm: HouseholdMember; originalIndex: number };

/**
 * Q1 + Q2 pool — members who meet citizenship/immigration criteria.
 * NonMN: any member who did not live in the US since 1996.
 * MN:    member applying for coverage in a financial-assistance application,
 *        with lawful presence, not in US since 1996, and unchanged immigration status.
 */
const getMembersForQ1 = (
    householdMembers: HouseholdMember[],
    isState: IsState,
    applyingForFinancialAssistanceIndicator: boolean | null
): EligibleMemberEntry[] =>
    householdMembers
        .map((hhm, originalIndex) => ({ hhm, originalIndex }))
        .filter(({ hhm }) =>
            questionsVisibility.isMilitaryServiceCitizenshipImmigrationCriteriaMet({ hhm, isState, applyingForFinancialAssistanceIndicator })
        );

/**
 * Q3 pool — members who are seeking coverage (MN only; always empty for NonMN).
 */
const getMembersForQ3 = (
    householdMembers: HouseholdMember[],
    isState: IsState
): EligibleMemberEntry[] =>
    householdMembers
        .map((hhm, originalIndex) => ({ hhm, originalIndex }))
        .filter(({ hhm }) =>
            questionsVisibility.isMilitaryServiceCoverageCriteriaMet({ hhm, isState })
        );

/**
 * Page2_25 - Military Service Information
 * @param props - SsapPage props
 * @returns ReactElement - Military service form page
 */
const Page2_25 = (props: SsapPage): ReactElement => {
    const {
        data,
        data: {
            applyingForFinancialAssistanceIndicator,
            taxHousehold: {
                thCommonParams: {
                    hasSomeoneHonorablyDischargedOrActiveDutyMilitary,
                    hasSomeoneReturnedFromTour,
                    militaryFamilyMember,
                }
            }
        },
        form,
        householdMembers,
        formName
    } = props;

    const { isFormSubmitted, isEligiblyForSave } = preventerTools.provideObject(form?.[formName]);

    const isState = useSelector(getIsState) as IsState;
    const serverDate = useSelector(getServerDate) as string;

    // ── Member lists ──────────────────────────────────────────────────────────
    const q1Members = getMembersForQ1(householdMembers, isState, applyingForFinancialAssistanceIndicator);

    // Q2: Q1-eligible members NOT selected on Q1 — recomputes reactively as Q1 selections change
    const q2Members = isState.MN ? q1Members.filter(
        ({ hhm }) => hhm.citizenshipImmigrationStatus?.honorablyDischargedOrActiveDutyMilitaryMemberIndicator !== true
    ) : [];

    // Q3: all members seeking coverage (MN only; always empty for NonMN)
    const q3Members = getMembersForQ3(householdMembers, isState);

    // ── Field change handler ──────────────────────────────────────────────────
    const onFieldChange = (...args: IFieldChangeArgs): void => {
        let { indexInArray, name, newData, value } = parseFieldChangeArgs(args);
        // @ts-ignore
        const member = newData.taxHousehold?.householdMember?.[indexInArray];

        switch (true) {
            case name === 'honorablyDischargedOrActiveDutyMilitaryMemberIndicator': {
                member.citizenshipImmigrationStatus.honorablyDischargedOrActiveDutyMilitaryMemberIndicator = value;

                // When a member is selected on Q1 (MN), clear their Q2 answer
                // since they will no longer appear in the Q2 list
                if (value === true && isState.MN) {
                    member.citizenshipImmigrationStatus.militaryFamilyMemberIndicator = null;
                }

                newData = dataSyncingTools.syncThCommonProps(newData, {
                    propName: 'military'
                });
                break;
            }

            case name === 'hasSomeoneHonorablyDischargedOrActiveDutyMilitary':
                newData = dataSyncingTools.syncThCommonProps(newData, {
                    isCommon: true,
                    propName: 'military',
                    customUpdater: (hhm: HouseholdMember): HouseholdMember => {
                        if (value === false) {
                            hhm.citizenshipImmigrationStatus.honorablyDischargedOrActiveDutyMilitaryMemberIndicator = false;
                        } else if (value === true) {
                            hhm.citizenshipImmigrationStatus.honorablyDischargedOrActiveDutyMilitaryMemberIndicator = null;
                        }

                        return hhm;
                    }
                });
                break;

            case name === 'returnedFromTourOfActiveDuty': {
                member.returnedFromTourOfActiveDuty = value;
                if (value === false) {
                    member.dateLastActiveTour = null;
                }
                newData = dataSyncingTools.syncThCommonProps(newData, {
                    propName: 'returnedFromTour'
                });
                break;
            }

            case name === 'dateLastActiveTour':
                member.dateLastActiveTour = value;
                break;

            case name === 'hasSomeoneReturnedFromTour': {
                newData = dataSyncingTools.syncThCommonProps(newData, {
                    isCommon: true,
                    propName: 'returnedFromTour',
                    customUpdater: (hhm: HouseholdMember): HouseholdMember => {
                        if (value === false) {
                            hhm.returnedFromTourOfActiveDuty = false;
                            hhm.dateLastActiveTour = null;
                        } else if (value === true) {
                            hhm.returnedFromTourOfActiveDuty = null;
                        }
                        return hhm;
                    }
                });
                break;
            }

            case name === 'militaryFamilyMemberIndicator': {
                member.citizenshipImmigrationStatus.militaryFamilyMemberIndicator = value;
                newData = dataSyncingTools.syncThCommonProps(newData, {
                    propName: 'spouseOrDependentOfVeteran'
                });
                break;
            }

            case name === 'militaryFamilyMember':
                newData = dataSyncingTools.syncThCommonProps(newData, {
                    isCommon: true,
                    propName: 'spouseOrDependentOfVeteran',
                    customUpdater: (hhm: HouseholdMember): HouseholdMember => {
                        if (value === false) {
                            hhm.citizenshipImmigrationStatus.militaryFamilyMemberIndicator = false;
                        } else if (value === true) {
                            hhm.citizenshipImmigrationStatus.militaryFamilyMemberIndicator = null;
                        }
                        return hhm;
                    }
                });
                break;
        }

        props.actions.changeData(newData);
    };

    // ── Validation ────────────────────────────────────────────────────────────

    const atLeastOneMemberSpouseOrDependent = q2Members.some(
        ({ hhm }) => hhm.citizenshipImmigrationStatus?.militaryFamilyMemberIndicator === true
    );

    const atLeastOneMemberReturnedFromTour = q3Members.some(
        ({ hhm }) => hhm.returnedFromTourOfActiveDuty === true
    );

    // Q1 required for MN: at least one member selected OR "None of the above" chosen
    const isQ1Valid = (): boolean => {
        if (!isState.MN) return true;
        return hasSomeoneHonorablyDischargedOrActiveDutyMilitary === false ||
            q1Members.some(({ hhm }) =>
                hhm.citizenshipImmigrationStatus?.honorablyDischargedOrActiveDutyMilitaryMemberIndicator === true
            );
    };

    // Q2 (MN only): valid when "None of the above" OR at least one member selected.
    // Does not require militaryFamilyMember === true since the aggregate flag may lag
    // behind individual selections on initial render of saved applications.
    const isQ2Valid = (): boolean => {
        if (!isState.MN || q2Members.length === 0) return true;
        return militaryFamilyMember === false || atLeastOneMemberSpouseOrDependent;
    };

    // Q3 selection valid (MN only): "None of the above" OR at least one member selected.
    // Does NOT validate the date — the date input is required, so Form's input-level
    // validation gates save independently. Does not require hasSomeoneReturnedFromTour === true
    // since the aggregate flag may lag on initial render of saved applications.
    const isQ3SelectionValid = (): boolean => {
        if (!isState.MN || q3Members.length === 0) return true;
        return hasSomeoneReturnedFromTour === false || atLeastOneMemberReturnedFromTour;
    };

    const isFormValid =
        // Only validate Q1 if Q1 is shown
        (q1Members.length === 0 || isQ1Valid()) &&
        // Only validate Q2 if Q2 is shown
        (q2Members.length === 0 || isQ2Valid()) &&
        // Q3 validation handles its own visibility logic
        isQ3SelectionValid();

    // ── Q1 fieldset — Honorably discharged / active duty ──────────────────────
    const getQ1Content = (): (ReactElement | null)[] => {
        const memberInputs = q1Members.map(({ hhm, originalIndex }: EligibleMemberEntry) => {
            const { citizenshipImmigrationStatus: { honorablyDischargedOrActiveDutyMilitaryMemberIndicator }, name, applicantGuid } = hhm;

            return (
                <UniversalInput
                    key={applicantGuid}
                    label={{
                        text: 'common.input.label.non_internationalized_text',
                        labelTextReplacements: { text: normalizeNameOfPerson(name) },
                        isLabelRequired: false
                    }}
                    inputData={{
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'honorablyDischargedOrActiveDutyMilitaryMemberIndicator',
                        checked: honorablyDischargedOrActiveDutyMilitaryMemberIndicator,
                        value: honorablyDischargedOrActiveDutyMilitaryMemberIndicator,
                        currentData: cloneObject(data),
                        isInputStandalone: true,
                        fields: [],
                    }}
                    inputActions={{ onFieldChange }}
                    indexInArray={originalIndex}
                />
            );
        });

        const noneOfAboveInput = (
            <UniversalInput
                key='noneHonorablyDischarged'
                label={{ text: 'ssap.input.label.none_of_above', isLabelRequired: false }}
                inputData={{
                    isInputRequired: false,
                    type: INPUT_TYPES.CHECKBOX,
                    name: 'hasSomeoneHonorablyDischargedOrActiveDutyMilitary',
                    checked: hasSomeoneHonorablyDischargedOrActiveDutyMilitary === false,
                    value: hasSomeoneHonorablyDischargedOrActiveDutyMilitary === false,
                    currentData: cloneObject(data),
                    fields: ['taxHousehold', 'thCommonParams'],
                    reversedValue: true
                }}
                inputActions={{ onFieldChange }}
            />
        );

        return [...memberInputs, noneOfAboveInput];
    };

    const militaryFieldSet = () => (
        <fieldset key="military-veteran-fieldset" className="margin-tb-10">
            <legend data-testid="military_service_details_legend">
                {(() => {
                    switch (true) {
                        case isState.NJ:
                            return (
                                <Fragment>
                                    <span id="military_service_details_legend_text">
                                        <FormattedMessage id="ssap.family_household.military_service.who_is_military" />
                                    </span>
                                    <div className="usa-alert gi-alert gi-alert__left-accent gi-alert__info">
                                        <div className="gi-alert__body">
                                            <p className="gi-alert__text">
                                                <FormattedMessage id="instructional_text.military_service_information" />
                                            </p>
                                        </div>
                                    </div>
                                </Fragment>
                            );
                        case isState.MN:
                            return (
                                <span className="required-true" id="military_service_details_legend_text">
                                    <FormattedMessage id="ssap.family_household.military_service.who_is_military" />
                                </span>
                            );
                        default:
                            return (
                                <InstructionalText
                                    Component={
                                        <p
                                            className="required-false"
                                            data-instructions="instructional_text.military_service_information"
                                            id="military_service_details_legend_text"
                                        >
                                            {'ssap.family_household.military_service.who_is_military'}
                                        </p>
                                    }
                                />
                            );
                    }
                })()}
            </legend>
            {isFormSubmitted && !isEligiblyForSave && !isQ1Valid() && (
                <ValidationErrorMessage
                    isVisible
                    messageText={VALIDATION_ERROR_MESSAGES.CHOSEN}
                    errorClasses={['margin-t-0', 'margin-l-0']}
                />
            )}
            <div role="group" aria-labelledby="military_service_details_legend_text">
                {getQ1Content()}
            </div>
        </fieldset>
    );

    // ── Q2 fieldset — Spouse / dependent of veteran (MN only) ─────────────────
    const getQ2SpouseDependentList = (): ReactElement[] => [
        ...q2Members.map(({ hhm, originalIndex }: EligibleMemberEntry) => {
            const { citizenshipImmigrationStatus: { militaryFamilyMemberIndicator }, name, applicantGuid } = hhm;

            return (
                <UniversalInput
                    key={applicantGuid}
                    label={{
                        text: 'common.input.label.non_internationalized_text',
                        labelTextReplacements: { text: normalizeNameOfPerson(name) }
                    }}
                    inputData={{
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'militaryFamilyMemberIndicator',
                        checked: militaryFamilyMemberIndicator,
                        value: militaryFamilyMemberIndicator,
                        currentData: cloneObject(data),
                        disabled: false,
                        fields: []
                    }}
                    inputActions={{ onFieldChange }}
                    indexInArray={originalIndex}
                />
            );
        }),
        <UniversalInput
            key='noneSpouseDependent'
            label={{ text: 'ssap.input.label.none_of_above', isLabelRequired: false }}
            inputData={{
                isInputRequired: false,
                type: INPUT_TYPES.CHECKBOX,
                name: 'militaryFamilyMember',
                checked: militaryFamilyMember === false,
                value: militaryFamilyMember === false,
                currentData: cloneObject(data),
                fields: ['taxHousehold', 'thCommonParams'],
                reversedValue: true
            }}
            inputActions={{ onFieldChange }}
        />
    ];

    const spouseDependentFieldset = () => (
        <fieldset key="spouse-dependent-veteran-fieldset" className="margin-tb-10">
            <ValidationErrorMessage
                isVisible={isFormSubmitted && !isEligiblyForSave && !isQ2Valid()}
                messageText={VALIDATION_ERROR_MESSAGES.CHOSEN}
                errorClasses={['margin-t-0', 'margin-l-0']}
            />
            <legend className="required-false" data-testid="spouse_dependent_veteran_legend">
                <InstructionalText
                    required
                    Component={
                        <p data-instructions="instructional_text.military_service_information" id="spouse_dependent_veteran_legend_text">
                            {'ssap.family_household.military_service.are_spouse_or_dependent_child_of_veteran'}
                        </p>
                    }
                />
            </legend>
            <div role="group" aria-labelledby="spouse_dependent_veteran_legend_text">
                {getQ2SpouseDependentList()}
            </div>
        </fieldset>
    );

    // ── Q3 fieldset — Returned from tour of active duty (MN only) ─────────────
    const getQ3ReturnedFromTourList = (): ReactElement[] => [
        ...q3Members.map(({ hhm, originalIndex }: EligibleMemberEntry) => {
            const { returnedFromTourOfActiveDuty, dateLastActiveTour, name, applicantGuid } = hhm;

            return (
                <Fragment key={applicantGuid}>
                    <UniversalInput
                        label={{
                            text: 'common.input.label.non_internationalized_text',
                            labelTextReplacements: { text: normalizeNameOfPerson(name) },
                            isLabelRequired: false
                        }}
                        inputData={{
                            isInputRequired: false,
                            isInputStandalone: true,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'returnedFromTourOfActiveDuty',
                            checked: returnedFromTourOfActiveDuty,
                            value: returnedFromTourOfActiveDuty,
                            currentData: cloneObject(data),
                            fields: [],
                        }}
                        inputActions={{ onFieldChange }}
                        indexInArray={originalIndex}
                    />
                    {returnedFromTourOfActiveDuty === true && (
                        <>
                            {/* Empty date → "Please provide a response here to continue" */}
                            <ValidationErrorMessage
                                isVisible={isFormSubmitted && !isEligiblyForSave && !dateLastActiveTour}
                                messageText={VALIDATION_ERROR_MESSAGES.CHOSEN}
                                errorClasses={['margin-l-0', 'padding-l-20-px']}
                            />
                            <UniversalInput
                                id='last_active_tour_date'
                                label={{
                                    labelClass: ['padding-l-20-px'],
                                    text: 'ssap.input.label.military_service.last_tour_end',
                                    isLabelRequired: true
                                }}
                                inputData={{
                                    divClass: ['padding-l-20-px'],
                                    isInputRequired: true,
                                    isInputStandalone: true,
                                    isValidationErrorMessageVisible: !!dateLastActiveTour,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'dateLastActiveTour',
                                    value: dateLastActiveTour,
                                    specificValidation: { validationType: VALIDATION_TYPES.LAST_24_MONTHS },
                                    valuesToMatch: [serverDate, TIME_INTERVALS.TWENTY_FOUR_MONTHS],
                                    validationType: VALIDATION_TYPES.LAST_24_MONTHS,
                                    validationErrorMessage: dateLastActiveTour === DATE_FORMAT_DASHED
                                        ? VALIDATION_ERROR_MESSAGES.DATE
                                        : VALIDATION_ERROR_MESSAGES.DATE_WITHIN_24_MONTHS,
                                    currentData: cloneObject(data),
                                    fields: [],
                                }}
                                inputActions={{ onFieldChange }}
                                indexInArray={originalIndex}
                                errorClasses={['margin-l-0']}
                            />
                        </>
                    )}
                </Fragment>
            );
        }),
        <UniversalInput
            key='noneReturnedFromTour'
            label={{ text: 'ssap.input.label.none_of_above', isLabelRequired: false }}
            inputData={{
                isInputRequired: false,
                isInputStandalone: true,
                type: INPUT_TYPES.CHECKBOX,
                name: 'hasSomeoneReturnedFromTour',
                checked: hasSomeoneReturnedFromTour === false,
                value: hasSomeoneReturnedFromTour === false,
                currentData: cloneObject(data),
                fields: ['taxHousehold', 'thCommonParams'],
                reversedValue: true
            }}
            inputActions={{ onFieldChange }}
        />
    ];

    const returnedFromTourFieldset = () => (
        <fieldset key="returned-from-tour-fieldset" className="margin-tb-10">
            <ValidationErrorMessage
                isVisible={isFormSubmitted && !isEligiblyForSave && !isQ3SelectionValid()}
                messageText={VALIDATION_ERROR_MESSAGES.CHOSEN}
                errorClasses={['margin-t-0', 'margin-l-0']}
            />
            <legend data-testid="returned_from_tour_legend">
                <span className="required-true">
                    <FormattedMessage id="ssap.family_household.military_service.returned_from_tour" />
                </span>
            </legend>
            {getQ3ReturnedFromTourList()}
        </fieldset>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="fade-in" id="fhMilitaryDuty">
            <div className="subsection">
                <Form {...{ formName, topLevelValidity: isFormValid }}>
                    {/* Q1 — all states */}
                    {q1Members.length > 0 && militaryFieldSet()}

                    {/* Q2 — MN only: spouse/dependent; hides when all Q1 members are selected on Q1 */}
                    {q2Members.length > 0 && spouseDependentFieldset()}

                    {/* Q3 — MN only: returned from tour; shown for all members seeking coverage */}
                    {q3Members.length > 0 && returnedFromTourFieldset()}
                </Form>
            </div>
        </div>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({
    data: state.ssap.data,
    form: state.ssap.form,
    householdMembers: state.ssap.data.taxHousehold.householdMember
});

const mapDispatchToProps = (dispatch: Dispatch): { actions: Partial<SsapReduxActions> } => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_25);
